import React, {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { DateTime } from 'luxon';
import { Link } from 'react-router-dom';
import { Chip, IconButton, Tooltip, Typography } from '@mui/material';
import type {
  GridApiPro,
  GridCallbackDetails,
  GridCellParams,
  GridColDef,
  GridPaginationModel,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import {
  DataGridPro,
  CellDetail,
  CellSingleValue,
  CellAddress,
} from '../../tables/DataGridPro';
import {
  AssignmentTurnedInOutlined as AssignmentTurnedInOutlinedIcon,
  Replay as ReplayIcon,
  EditCalendar as EditCalendarIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  PublishedWithChanges as PublishedWithChangesIcon,
} from '@mui/icons-material';

import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { useIsPR1User } from '../../../hooks/permissions/useIsPR1User';
import { useIsPR2User } from '../../../hooks/permissions/useIsPR2User';

import type {
  TripLeg,
  TripLegAddress,
} from '../../../data/models/reservation.model';
import {
  TripLegStatus,
  TripLegStatusBC,
  tripStatusAllocatedList,
  tripStatusWithEtaList,
} from '../../../data/models/trip-leg-status.model';
import type { TransportationAttribute } from '../../../data/models/transportation-attribute.model';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

import dateUtils from '../../../services/DateUtils';
import triplegUtils from '../../../services/triplegUtils';

import * as triplegSlice from '../../../data/tripleg.slice';
import * as lookupSlice from '../../../data/lookup.slice';
import * as accountingSlice from '../../../data/accounting.slice';
import * as providerSlice from '../../../data/provider.slice';
import * as driverSlice from '../../../data/driver.slice';

import AssignProviderDialog from './AssignProviderDialog';
import ReopenTripLegDialog from './ReopenTripLegDialog';
import EditTripLegTimesDialog from './EditTripLegTimesDialog';
import UnassignProviderDialog from './UnassignProviderDialog';

const chipStyles = {
  textTransform: 'uppercase',
};

interface TripLegListProps {
  page: number;
  pageSize: number;
  pageSizeOptions: number[];
  rowCount: number;
  paginationChange: (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => void;
  sortModelChange: (
    model: GridSortModel,
    details: GridCallbackDetails<any>,
  ) => void;
  tripLegs: TripLeg[];
  loading: boolean;
  isSelectingTrips: boolean;
  showAssignAction?: boolean;
  showReassignAction?: boolean;
  showReopenAction?: boolean;
  showUnassignAction?: boolean;
  showInProgress?: boolean;
  showEditTimesAction?: boolean;
  dataGridApiRef: MutableRefObject<GridApiPro>;
}

const TripLegInventoryTable = ({
  page,
  pageSize,
  pageSizeOptions,
  rowCount,
  tripLegs,
  paginationChange,
  sortModelChange,
  loading,
  isSelectingTrips,
  showAssignAction = true,
  showReassignAction = true,
  showReopenAction = true,
  showUnassignAction = true,
  showInProgress = false,
  showEditTimesAction = false,
  dataGridApiRef,
}: TripLegListProps) => {
  const t = useCommonTranslation();
  const isSuperReaderUser = useIsSuperReaderUser();
  const isPR1User = useIsPR1User();
  const isPR2User = useIsPR2User();

  const dispatch = useAppDispatch();

  const [reassigningTripLeg, setReassigningTripLeg] = React.useState(false);

  const [dialogAssignOpen, setDialogAssignOpen] = React.useState(false);
  const [dialogReopenOpen, setDialogReopenOpen] = React.useState(false);
  const [dialogEditTimesOpen, setDialogEditTimesOpen] = React.useState(false);
  const [dialogUnassignOpen, setDialogUnassignOpen] = React.useState(false);

  const [selectedTripLeg, setSelectedTripLeg] = React.useState<TripLeg>();

  const timezones = useAppSelector(lookupSlice.selectTimeZones);
  const transportationAttributes = useAppSelector(
    accountingSlice.selectTransportationAttributes,
  );
  const driverLocationsById = useAppSelector(
    driverSlice.selectDriverLocationsById,
  );

  const passengerCareLevels = useAppSelector(
    lookupSlice.selectPassengerCareLevels,
  );

  const savedProviderCallOutcome = useAppSelector(
    providerSlice.selectSavedProviderCallOutcome,
  );

  const isSavedTripLeg = useAppSelector(triplegSlice.selectSavedTripLeg);

  const tripModeBCs = useAppSelector(lookupSlice.selectTripModes);

  const getAttributeByUUID = (uuid: string) => {
    return transportationAttributes.find(
      (a) => a.transportationAttributeUUID === uuid,
    );
  };

  const inProgressDriverIds = useMemo(() => {
    const ids = new Set<number>();
    if (!tripLegs) return [] as number[];
    tripLegs.forEach((tl: any) => {
      const tripLegStatus = tl.tripLegStatusBC
        ? tl.tripLegStatusBC
        : tl.tripLeg?.tripLegStatusBC;
      const status = tripLegStatus?.toLowerCase();
      if (
        status &&
        tripStatusWithEtaList.includes(status as TripLegStatus) &&
        tl.driverID
      ) {
        ids.add(Number(tl.driverID));
      }
    });
    return Array.from(ids);
  }, [tripLegs]);

  useEffect(() => {
    inProgressDriverIds.forEach((driverId) => {
      dispatch(driverSlice.fetchDriverLocationAsync(driverId));
    });
  }, [dispatch, inProgressDriverIds]);

  const getAttributes = (
    tripLeg: TripLeg,
    attrBC: string,
  ): TransportationAttribute[] => {
    const attrs: TransportationAttribute[] = [];
    if (tripLeg.transportationAttributes) {
      tripLeg.transportationAttributes.forEach((ta) => {
        let attr = getAttributeByUUID(ta[0]);
        if (attr?.transportationAttributeCategoryBC === attrBC) {
          attrs.push(attr);
          for (let i = 1; i < ta.length; i++) {
            attr = getAttributeByUUID(ta[i]);
            if (attr) {
              attrs.push(attr);
            }
          }
        }
      });
    }
    return attrs;
  };

  const getPickupTime = useCallback(
    (params: GridCellParams) => {
      const tripLeg = params.row;
      if (tripLeg.isWillCall) {
        if (tripLeg.appointmentDate) {
          // Since the date has no time, we parse it and add 11 hours to ensure it displays the expected date.
          // If we didn't do this it would display the previous day since it parsed as a UTC date.
          const m = DateTime.fromISO(tripLeg.appointmentDate)
            .setZone(dateUtils.WORK_TIME_ZONE)
            .plus({ hours: 11 });
          return (
            m.toLocaleString(DateTime.DATE_SHORT) +
            ', ' +
            t('reservation.willCallTime')
          );
        } else {
          return '';
        }
      } else {
        if (tripLeg.pickUpDueTimeUTC) {
          const tz = timezones.find(
            (t) => t.timeZoneID === tripLeg.pickUpAddressTimeZoneID,
          );
          const m = DateTime.fromISO(tripLeg.pickUpDueTimeUTC, {
            zone: tz?.ianaStandard,
          });
          return m.toLocaleString(DateTime.DATETIME_SHORT);
        } else {
          return '';
        }
      }
    },
    [tripLegs],
  );

  const getPassengerCareLevel = (params: GridCellParams): string => {
    const tripLeg = params.row;
    const passengerCareLevel = passengerCareLevels.find(
      (pcl) => pcl.passengerCareLevelBC === tripLeg.passengerCareLevelBC,
    );
    return passengerCareLevel?.passengerCareLevelName || '';
  };

  const renderPickupTime = (params: GridCellParams): React.ReactElement => {
    const pickupTime = getPickupTime(params);
    return <CellSingleValue>{pickupTime}</CellSingleValue>;
  };

  const renderMember = (params: GridCellParams): React.ReactElement => {
    const passengerFirstName = params.row.passengerFirstName;
    const passengerLastName = params.row.passengerLastName;
    return (
      <CellSingleValue>{`${passengerFirstName} ${passengerLastName}`}</CellSingleValue>
    );
  };

  const renderPickupAddress = (params: GridCellParams): React.ReactElement => {
    const tripLeg = params.row;
    const address = {
      addressLine1: tripLeg.pickUpAddressLine1,
      city: tripLeg.pickUpCity,
      stateCode: tripLeg.pickUpStateCode,
      zipCode: tripLeg.pickUpZipCode,
    };
    return <CellAddress address={address} />;
  };

  const renderDropoffAddress = (params: GridCellParams): React.ReactElement => {
    const tripLeg = params.row;
    const address = {
      addressLine1: tripLeg.dropOffAddressLine1,
      city: tripLeg.dropOffCity,
      stateCode: tripLeg.dropOffStateCode,
      zipCode: tripLeg.dropOffZipCode,
    };
    return <CellAddress address={address} />;
  };

  const renderETA = (params: GridCellParams): React.ReactElement => {
    const tripLeg = params.row;

    const driverId = Number(tripLeg.driverID);
    const location = driverId ? driverLocationsById[driverId] : undefined;
    const seconds = location?.estimatedTimeOfArrivalSeconds;
    const eta = seconds
      ? `${Math.round(Number(seconds) / 60)} ${t('common.minutes')}`
      : '';
    return <>{eta}</>;
  };

  const renderTripId = (params: GridCellParams): React.ReactElement => {
    const tripLeg = params.row;
    if (!tripLeg) {
      return <></>;
    }
    const tripId = params.row.tripLegReferenceID;
    const passengerUUID = params.row.passengerUUID;
    const reservationID = params.row.reservationReferenceID;
    const link = `/passengers/${passengerUUID}/reservations/${reservationID}?tripLegID=${tripId}`;

    const tripIdText = <span style={{ fontWeight: 700 }}>{tripId}</span>;

    return (
      <CellSingleValue>
        <Link to={link}>{tripIdText}</Link>
      </CellSingleValue>
    );
  };

  const renderStatus = (params: GridCellParams): React.ReactElement => {
    const tripLegState = params.row.tripLegStatusBC;
    const chipColor = canReopenTripLeg(params.row) ? 'error' : 'primary';
    return tripLegState ? (
      <Chip color={chipColor} label={tripLegState} sx={chipStyles} />
    ) : (
      <></>
    );
  };
  const tripModes = useAppSelector(lookupSlice.selectTripModes);
  const renderTripMode = (params: GridCellParams): React.ReactElement => {
    var tripMode = tripModes.find(
      (m) => m.tripModeBC === params.row.tripModeBC,
    )?.tripModeName;
    return <CellDetail>{tripMode}</CellDetail>;
  };

  const renderPassengerCareLevel = (
    params: GridCellParams,
  ): React.ReactElement => {
    const passengerCarelevel = getPassengerCareLevel(params);
    return <CellDetail>{passengerCarelevel}</CellDetail>;
  };

  const renderProviderName = (params: GridCellParams): React.ReactElement => {
    const provider = params.row.providerName;
    return <CellDetail>{provider}</CellDetail>;
  };

  const assignTripLeg = (tripLeg: TripLeg) => {
    setSelectedTripLeg(tripLeg);
    setReassigningTripLeg(false);
    setDialogAssignOpen(true);
  };

  const reassignTripLeg = (tripLeg: TripLeg) => {
    setSelectedTripLeg(tripLeg);
    setReassigningTripLeg(true);
    setDialogAssignOpen(true);
  };

  const reopenTripLeg = (tripLeg: TripLeg) => {
    setSelectedTripLeg(tripLeg);
    setDialogReopenOpen(true);
  };

  const canAssignTripLeg = (tripLeg: TripLeg) => {
    if (isSuperReaderUser) {
      return false;
    }
    if (tripLeg.tripLegStatusBC === TripLegStatusBC.Open) {
      return true;
    }
    return false;
  };

  const canReassignTripLeg = (tripLeg: TripLeg) => {
    if (isSuperReaderUser) {
      return false;
    }
    if (tripLeg.tripLegStatusBC === TripLegStatusBC.Accepted) {
      return true;
    }
    return false;
  };

  const canEditTripTimes = (tripLeg: TripLeg) => {
    if (isSuperReaderUser) {
      return false;
    }
    if (tripLeg.tripLegStatusBC === TripLegStatusBC.Accepted) {
      return true;
    }
    return false;
  };

  const canReopenTripLeg = (tripLeg: TripLeg) => {
    if (isSuperReaderUser) {
      return false;
    }

    return triplegUtils.canReopenTripLeg(tripLeg);
  };

  const canUnassignTripLeg = (tripLeg: TripLeg) => {
    if (isSuperReaderUser) {
      return false;
    }

    if (
      (isPR1User || isPR2User) &&
      tripLeg.transportationProviderID &&
      tripStatusAllocatedList.includes(
        tripLeg.tripLegStatusBC?.toLowerCase() as TripLegStatus,
      )
    ) {
      return true;
    } else {
      return false;
    }
  };

  const editTripTimes = (tripLeg: TripLeg) => {
    setSelectedTripLeg(tripLeg);
    setDialogEditTimesOpen(true);
  };

  const closeAssignDialog = () => {
    setDialogAssignOpen(false);
  };

  const closeReopenDialog = () => {
    setDialogReopenOpen(false);
  };
  const closeEditTimesDialog = () => {
    setDialogEditTimesOpen(false);
  };
  const closeUnassignDialog = () => {
    setDialogUnassignOpen(false);
  };

  const unassignTripLeg = (tripLeg: TripLeg) => {
    setSelectedTripLeg(tripLeg);
    setDialogUnassignOpen(true);
  };

  const handleConfirmUnassignTripLeg = (tripLeg: TripLeg) => {
    dispatch(
      triplegSlice.rejectTripLegAsync({
        passengerUUID: tripLeg.passengerUUID,
        tripLegReferenceID: tripLeg.tripLegReferenceID!,
        tripLegRejectedReasonBC: TripLegStatusBC.Rejected,
      }),
    );
    closeUnassignDialog();
  };

  const columns: GridColDef[] = [
    {
      field: 'tripLegReferenceID',
      headerName: t('triplegInventory.tripId'),
      width: 80,
      renderCell: renderTripId,
    },
    {
      field: 'pickUpDueTimeUTC',
      headerName: t('triplegInventory.pickupTime'),
      width: 150,
      sortable: true,
      renderCell: renderPickupTime,
    },
    {
      field: 'member',
      headerName: t('triplegInventory.member'),
      flex: 1,
      sortable: false,
      renderCell: renderMember,
    },
    {
      field: 'pickUpAddress',
      headerName: t('triplegInventory.pickupAddress'),
      flex: 1.5,
      sortable: false,
      renderCell: renderPickupAddress,
    },
    {
      field: 'dropOffAddress',
      headerName: t('triplegInventory.dropoffAddress'),
      flex: 1.5,
      sortable: false,
      renderCell: renderDropoffAddress,
    },
    {
      field: 'tripModeName',
      headerName: t('triplegInventory.tripMode'),
      flex: 1,
      renderCell: renderTripMode,
    },
    {
      field: 'passengerCareLevelName',
      headerName: t('triplegInventory.passengerCareLevel'),
      flex: 1,
      renderCell: renderPassengerCareLevel,
    },
    {
      field: 'providerName',
      headerName: t('triplegInventory.providerName'),
      flex: 1,
      renderCell: renderProviderName,
    },
    {
      field: 'tripLegStatusBC',
      headerName: t('triplegInventory.status'),
      width: 140,
      renderCell: renderStatus,
    },
    {
      field: 'actions',
      headerName: ' ',
      width: 120,
      sortable: false,
      filterable: false,
      align: 'right',
      renderCell: (params: GridCellParams) => (
        <>
          {showReopenAction && canReopenTripLeg(params.row) && (
            <Tooltip title={t('triplegInventory.reopen')}>
              <span>
                <IconButton
                  data-testid="reopen"
                  onClick={(_evt) => reopenTripLeg(params.row)}
                  size="medium"
                  sx={{
                    visibility: canReopenTripLeg(params.row)
                      ? 'visible'
                      : 'hidden',
                  }}
                  color="error">
                  <ReplayIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {showReassignAction && canReassignTripLeg(params.row) && (
            <Tooltip title={t('triplegInventory.reassign')}>
              <span>
                <IconButton
                  data-testid="reassign"
                  onClick={(_evt) => reassignTripLeg(params.row)}
                  size="medium"
                  color="primary">
                  <PublishedWithChangesIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {showAssignAction && canAssignTripLeg(params.row) && (
            <Tooltip title={t('triplegInventory.assign')}>
              <span>
                <IconButton
                  data-testid="edit"
                  disabled={params.row.tripLegStatusBC !== 'OPEN'}
                  onClick={(_evt) => assignTripLeg(params.row)}
                  size="medium"
                  color="primary">
                  <AssignmentTurnedInOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {showEditTimesAction &&
            !isSuperReaderUser &&
            canEditTripTimes(params.row) && (
              <Tooltip title={t('triplegInventory.editEventTimes')}>
                <span>
                  <IconButton
                    data-testid="edit"
                    onClick={(_evt) => editTripTimes(params.row)}
                    size="medium"
                    color="primary">
                    <EditCalendarIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          {showUnassignAction && canUnassignTripLeg(params.row) && (
            <Tooltip title={t('triplegInventory.unassign')}>
              <span>
                <IconButton
                  data-testid="unassign"
                  onClick={(_evt) => unassignTripLeg(params.row)}
                  size="medium"
                  color="error">
                  <RemoveCircleOutlineIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  if (showInProgress) {
    const estimatedTimeColumn: GridColDef = {
      field: 'driverEstimatedTime',
      headerName: t('triplegInventory.eta'),
      width: 180,
      sortable: false,
      renderCell: renderETA,
      valueGetter: (params: GridCellParams) => {
        const tripLegStatus = params.row.tripLegStatusBC?.toLowerCase();
        if (!tripStatusWithEtaList.includes(tripLegStatus as TripLegStatus)) {
          return '';
        }

        return params.row.driverEstimatedTime;
      },
    };
    columns.splice(1, 0, estimatedTimeColumn);
  }

  useEffect(() => {
    if (savedProviderCallOutcome) {
      setDialogAssignOpen(false);
    }
  }, [savedProviderCallOutcome]);

  useEffect(() => {
    if (isSavedTripLeg) {
      setDialogUnassignOpen(false);
    }
  }, [isSavedTripLeg]);

  return (
    <>
      <DataGridPro
        getRowId={(row) => `${row.tripLegReferenceID}`}
        apiRef={dataGridApiRef}
        rows={tripLegs}
        rowCount={rowCount}
        columns={columns}
        pagination={true}
        paginationModel={{ page: page - 1, pageSize }}
        paginationMode="server"
        pageSizeOptions={pageSizeOptions}
        onPaginationModelChange={paginationChange}
        onSortModelChange={sortModelChange}
        autoHeight={true}
        loading={loading}
        checkboxSelection={isSelectingTrips}
        disableColumnSelector={true}
        hideFooter={false}
        sortingMode="server"
        sortingOrder={['asc', 'desc']}
        initialState={{
          sorting: {
            sortModel: [{ field: 'date', sort: 'asc' }],
          },
        }}
      />
      {selectedTripLeg && dialogAssignOpen && (
        <AssignProviderDialog
          tripLeg={selectedTripLeg}
          open={dialogAssignOpen}
          close={closeAssignDialog}
          reassigningTripLeg={reassigningTripLeg}
        />
      )}
      {selectedTripLeg && dialogReopenOpen && (
        <ReopenTripLegDialog
          tripLeg={selectedTripLeg}
          open={dialogReopenOpen}
          close={closeReopenDialog}
        />
      )}
      {selectedTripLeg && dialogEditTimesOpen && (
        <EditTripLegTimesDialog
          tripLeg={selectedTripLeg}
          open={dialogEditTimesOpen}
          close={closeEditTimesDialog}
        />
      )}
      {selectedTripLeg && dialogUnassignOpen && (
        <UnassignProviderDialog
          tripLeg={selectedTripLeg}
          open={dialogUnassignOpen}
          close={closeUnassignDialog}
        />
      )}
    </>
  );
};

export { TripLegInventoryTable };
